import { Slot } from "@radix-ui/react-slot";
import { forwardRef, type ButtonHTMLAttributes } from "react";

import { cn } from "../../lib/utils";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

const VARIANTS: Record<Variant, string> = {
  primary: "border-transparent bg-accent text-accent-ink shadow-sm hover:brightness-110",
  secondary: "border-rule bg-raised text-ink hover:border-ink/25 hover:bg-sunken",
  ghost: "border-transparent bg-transparent text-ink hover:bg-ink/[0.06]",
  danger: "border-owing/35 bg-raised text-owing hover:bg-owing/10 hover:border-owing/60",
};

const SIZES: Record<Size, string> = {
  sm: "h-9 rounded-lg px-3 text-sm",
  md: "h-11 rounded-lg px-4 text-[15px]",
  lg: "h-13 rounded-xl px-6 text-base",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = "secondary", size = "md", asChild, ...props },
  ref,
) {
  const Component = asChild ? Slot : "button";
  return (
    <Component
      ref={ref}
      className={cn(
        "inline-flex select-none items-center justify-center gap-2 border font-medium",
        "transition-all duration-150 active:scale-[0.98]",
        "disabled:pointer-events-none disabled:opacity-45",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    />
  );
});
